import { is, path, isEmpty, dissocPath } from 'ramda'
import moment from 'moment'
import { camelCase } from 'change-case'
import { BUSINESS_STATUSES, DATE_FORMAT } from '@/lib/consts.js'

export function __t (s) {
  return s
}

/*
 * converts list of names to object, similar
 * to TypeScript enum
 * eg. ['a', 'b'] produces: { a: 'a', b: 'b' }
 * @param Array<string> names
 * @returns { [key:string]: string } enum object (frozen)
 */
export function makeEnum (names) {
  return Object.freeze(
    names.reduce((o, name) => {
      o[name] = name
      return o
    }, {})
  )
}

/*
 * deletes object prop. by path & its
 * empty ancestors
 *
 * example:
 *   delWithClear(
 *     { a: { b: { c: 1 } }, d: 2 },
 *     'a', 'b', 'c'
 *   )
 *   { d: 2 )
 */
export function delWithClear (o, ...p) {
  // shallow clone of an object, omitting
  // the property at the given path
  let _o = dissocPath(p, o)

  p.pop()

  while (p.length) {
    const t = path(p, _o)
    if (!isEmpty(t)) { break }
    _o = dissocPath(p, _o)
    p.pop()
  }

  return _o
}

export function distinct (o, ...paths) {
  let v = null
  if (is(Object, o)) {
    for (const p of paths) {
      v = path(p, o)
      if (v) { break }
    }
  }
  return v
}

export function debounce (fn, wait, immediate) {
  let timeout
  return function () {
    const context = this
    const args = arguments
    const later = function () {
      timeout = null
      if (!immediate) fn.apply(context, args)
    }
    const callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) fn.apply(context, args)
  }
}

export function hasFormErrors (errors, form) {
  if (!errors) return false
  let hasErrors = false

  for (const error of errors) {
    if (error.field && Object.keys(form).includes(error.field?.split('.')[0])) {
      hasErrors = true
      break
    }
  }

  return hasErrors
}

function currencyFormat (format) {
  return {
    pos: format,
    neg: `-${format}`,
    zero: format,
  }
}

export function Euro (symbol, space = true, after = true, precision = 2) {
  return {
    currency: {
      symbol,
      format: after
        ? (space ? currencyFormat('%v %s') : currencyFormat('%v%s'))
        : (space ? currencyFormat('%s %v') : currencyFormat('%s%v')),
      decimal: ',',
      thousand: '.',
      precision,
    },
    number: {
      precision: 0,
      thousand: '.',
      decimal: ',',
    },
  }
}

export function Dollar (symbol, space = false, after = false, precision = 2) {
  return {
    currency: {
      symbol,
      format: after
        ? (space ? currencyFormat('%v %s') : currencyFormat('%v%s'))
        : (space ? currencyFormat('%s %v') : currencyFormat('%s%v')),
      decimal: '.',
      thousand: ',',
      precision,
    },
    number: {
      precision: 0,
      thousand: ',',
      decimal: '.',
    },
  }
}

export function arrayMove (arr, old_index, new_index) {
  if (new_index >= arr.length) {
    var k = new_index - arr.length + 1
    while (k--) {
      arr.push(undefined)
    }
  }
  arr.splice(new_index, 0, arr.splice(old_index, 1)[0])
  return arr // for testing
}

export function calculateHeight (root, offset) {
  const height = root.height || window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight
  return `maxHeight: ${height - offset}px`
}

export function calculateWidth (root, offset) {
  const width = root.width || window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth
  return `width: ${width - offset}px`
}

export function hourToMomentDate (hour) {
  return hour ? moment(`${moment().format(DATE_FORMAT)}T${hour}`) : null
}

export function blobDownloader (blob, fileName = 'booksy-file.pdf') {
  if (!(blob instanceof Blob)) {
    throw new Error('Blob has wrong format')
  }

  if (!fileName.includes('.pdf')) {
    throw new Error('Blob name is wrong')
  }

  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(blob)
    return
  }
  const data = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = data
  link.download = fileName
  link.click()
  setTimeout(function () {
    window.URL.revokeObjectURL(data)
  }, 100)
}

export function isConfiguredBusiness (business) {
  const businessId = path(['id'], business)
  const businessStatus = path(['status'], business)

  return Boolean(businessId && businessStatus !== BUSINESS_STATUSES.SETUP)
}

export function isPromise (object) {
  try {
    return typeof object.then === 'function'
  } catch (e) {
    return false
  }
}

export function checkVersionOfMobileiOS () {
  const agent = window.navigator.userAgent
  const start = agent.indexOf('iPhone OS ')
  if (agent.indexOf('iPhone') > -1 && start > -1) {
    return Number(agent.substr(start + 10, 3).replace('_', '.'))
  }
  return 0
}

export const BROWSER = {
  EDGE: { icon: 'edge', label: 'Edge' },
  OPERA: { icon: 'opera', label: 'Opera' },
  CHROMIUM: { icon: 'chromium', label: 'Chromium' },
  CHROME: { icon: 'chrome', label: 'Chrome' },
  FIREFOX: { icon: 'firefox', label: 'Firefox' },
  SAFARI: { icon: 'safari', label: 'Safari' },
  BRAVE: { icon: 'brave', label: 'Brave' },
  SAMSUNG_INTERNET: { icon: 'samsung-internet', label: 'Samsung Internet' },
  VIVALDI: { icon: 'vivaldi', label: 'Vivaldi' },
  OTHER: { icon: 'web', label: 'Browser' },
}

export function getBrowser () {
  const agent = window.navigator.userAgent.toLowerCase()

  let browser = BROWSER.OTHER
  let version = 'Unknown'

  if (/opr|opt/i.test(agent)) {
    browser = BROWSER.OPERA
    version = agent.match(/(?:opr|opt)\/(\d+(\.\d+)?)/)?.[1]
  } else if (/edg/i.test(agent)) {
    browser = BROWSER.EDGE
    version = agent.match(/edg\/(\d+(\.\d+)?)/)?.[1]
  } else if (/chrome|chromium|crios/i.test(agent)) {
    browser = BROWSER.CHROME
    version = agent.match(/(?:chrome|chromium|crios)\/(\d+(\.\d+)?)/)?.[1]
  } else if (/firefox|fxios/i.test(agent)) {
    browser = BROWSER.FIREFOX
    version = agent.match(/(?:firefox|fxios)\/(\d+(\.\d+)?)/)?.[1]
  } else if (/safari/i.test(agent)) {
    browser = BROWSER.SAFARI
    version = agent.match(/version\/(\d+(\.\d+)?)/)?.[1]
  }

  return {
    ...browser,
    version: version || 'Unknown',
  }
}

export function displayComposableErrorToast (error, toast, logger) {
  const errors = error.response?.data?.errors
  const description = Array.isArray(errors) && errors[0]?.description
  const errorMessage = description || error

  toast.error(errorMessage)
  logger.error(errorMessage)
}

export function createThrottler (delay) {
  let queue = []
  let timeout = null

  const onTimeout = () => {
    clearTimeout(timeout)
    timeout = null

    const lastAction = queue.pop()
    queue = []

    if (typeof lastAction === 'function') {
      lastAction()
    }
  }

  return function (action) {
    queue.push(action)

    if (timeout) {
      return
    }

    timeout = setTimeout(onTimeout, +delay || 0)
  }
}

export function transformKeysToCamelCase (obj) {
  return Object.keys(obj).reduce((acc, key) => {
    return {
      [camelCase(key)]: obj[key],
      ...acc,
    }
  }, {})
}

export function extractFirstDescriptionFromApiError (error) {
  const errors = error?.response?.data?.errors
  if (errors && Array.isArray(errors)) {
    for (const error of errors) {
      if (error?.description) return error.description
    }
  }
  return null
}

export function transformCamelCaseToSnakeCase (str) {
  return str.replace(/([A-Z])/g, '_$1').toLowerCase()
}
